/**
borrowed from tmn-ferp-ui
*/

import { isFunction } from '@trumedianetworks/tmn-core-components/src/util/function-helpers';
import { store } from './store';
import { bindActionCreators } from 'redux';

export const createConnector = (store) => (mapStateToProps, actions, debug) => (baseElement) => {
    class connectedClass extends baseElement {
        constructor() {
            super();
            if (actions) {
                if (isFunction(actions)) {
                    actions = actions();
                }
                const mapDispatchToActions = (dispatch) => bindActionCreators(actions, dispatch);
                const newActions = mapDispatchToActions(store.dispatch);
                // merge with existing actions from base classes, using "this" rather than super
                // avoids issues when the direct parent doesn't have actions but the "grandparent" does
                if (this.actions) {
                    this.actions = {
                        ...this.actions,
                        ...newActions,
                    };
                } else {
                    this.actions = newActions;
                }
            }
        }

        connectedCallback() {
            if (super.connectedCallback) {
                super.connectedCallback();
            }
            this._storeUnsubscribe = store.subscribe(() => this.stateChanged(store.getState()));
            this.stateChanged(store.getState());
        }

        disconnectedCallback() {
            this._storeUnsubscribe();
            if (super.disconnectedCallback) {
                super.disconnectedCallback();
            }
        }

        updated(changedProps) {
            const state = store.getState();
            const props = this._handleMapStateToProps(state);
            if (props) {
                const propKeys = new Set(Object.keys(props));
                const changedPropsKeys = [...changedProps.keys()];
                const nonMapStatePropChanged = changedPropsKeys.some((changedKey) => {
                    return !propKeys.has(changedKey);
                });
                // Only update the component properties if a property not from mapStateToProps has changed.
                // Without this check, we could get caught in an endless rerender loop if some mapStateToProps
                // property always returns a new object instance
                if (nonMapStatePropChanged) {
                    propKeys.forEach((key) => {
                        this[key] = props[key];
                    });
                }
            }
            super.updated(changedProps);
        }

        /**
         * The `stateChanged(state)` method will be called when the state is updated.
         */
        stateChanged(state) {
            // note this is different from the pwa one. Had to add the super call b/c we are trying to connect
            // later in the game (i.e. after the main class is defined, so the main element class is the super)
            if (super.stateChanged) {
                super.stateChanged(state);
            }
            // if (debug) {
            //     console.log(debug, state);
            // }
            const props = this._handleMapStateToProps(state);
            if (props) {
                const propKeys = Object.keys(props);
                propKeys.forEach((key) => {
                    this[key] = props[key];
                });
            }
        }
        // this can be used to "reset" a commponent and re-run its first-run setup
        // functions. i.e. if the props that setup depends on have changed
        // note that while this works, if you are using a connected component that
        // has nested connected components that rely on the same properties (i.e. pageDescriptorName),
        // they would likely need to be re-connected as well, so be careful.
        reConnect(mapStateToProps) {
            this._mapStateToProps = false;
            if (this.prototype._mapStateProxys && this.prototype._mapStateProxys.length > 1) {
                // remove last (the previous one associated with this class), then remake with new one
                // the idea is to keep the previous superclasseses fns, but replace this classes fn
                this.prototype._mapStateProxys.pop();
                this.prototype._mapStateProxys = [
                    ...this.prototype._mapStateProxys,
                    mapStateToProps,
                ];
            } else {
                // all other cases remake with this one
                this.prototype._mapStateProxys = [mapStateToProps];
            }
        }

        // well it's not pretty but I think it works
        _handleMapStateToProps(state) {
            if (this._mapStateToProps) {
                return this._mapStateToProps(state);
            }

            if (this._mapStateProxys && !this._mapStateToProps) {
                // first run
                const localMapStateFns = [];
                let firstRunProps = {};
                this._mapStateProxys.forEach((mappingFn) => {
                    let propsOrFn = mappingFn(state, this);
                    let newProps;
                    if (isFunction(propsOrFn)) {
                        localMapStateFns.push(propsOrFn);
                        newProps = propsOrFn(state, this);
                    } else {
                        localMapStateFns.push(mappingFn);
                        newProps = propsOrFn;
                    }
                    firstRunProps = { ...firstRunProps, ...newProps };
                });

                this._mapStateToProps = (state) => {
                    return localMapStateFns.reduce((prevProps, mappingFn) => {
                        const props = mappingFn(state, this);
                        return { ...prevProps, ...props };
                    }, {});
                };
                return firstRunProps;
            }
        }
    } //end connectedClass

    // note these are outside connectedClass
    if (mapStateToProps) {
        //connectedClass.prototype._mapStateToPropsProxy = _mapStateToPropsProxy;
        if (connectedClass.prototype._mapStateProxys) {
            connectedClass.prototype._mapStateProxys = [
                ...connectedClass.prototype._mapStateProxys,
                mapStateToProps,
            ];
        } else {
            connectedClass.prototype._mapStateProxys = [mapStateToProps];
        }
    }

    return connectedClass;
};

export const connect = createConnector(store);
